import type { Dayjs } from 'dayjs'

import type {
  IncomeExpenseDetailResponse,
  OverallIncomeExpenseCurveItem,
  OverallIncomeExpenseCurveParams,
  OverallIncomeExpenseCurveResponse,
} from './types/overall-income-expense'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { DateRanges, dayjs } from '#/utils'

export const overallIncomeExpenseApi = defineApi('operation', ({ prefix }) => {
  /**
   * 整体收支曲线
   * POST /gobestads/operation/customerFundChangeDaily/queryOverallIncomeExpense
   */
  function getOverallIncomeExpenseCurve(
    params: {
      dateRange: [Dayjs, Dayjs] | string[] // 支持 Dayjs 对象或字符串数组
    } & OverallIncomeExpenseCurveParams,
  ): Promise<OverallIncomeExpenseCurveResponse> {
    const dateRange = DateRanges.dates(params.dateRange)!

    return requestClient
      .post<OverallIncomeExpenseCurveItem[]>(
        prefix('customerFundChangeDaily/queryOverallIncomeExpense'),
        {
          ...params,
          dateRange,
        },
      )
      .then((res) => {
        if (!res || res.length === 0) {
          return []
        }

        // 按时间周期排序
        const sortedData = [...res].sort((a, b) => {
          return dayjs(a.period).valueOf() - dayjs(b.period).valueOf()
        })

        // 返回整理后的数据
        return sortedData.map((item) => ({
          period: item.period,
          claimedIncome: item.claimedIncome || 0,
          adIncome: item.adIncome || 0,
          adExpense: item.adExpense || 0,
          adSpend: item.adSpend || 0,
          adProfit: item.adProfit || 0,
        }))
      })
  }

  /**
   * 整体收支明细
   * POST /gobestads/operation/customerFundChangeDaily/queryIncomeExpenseDetail
   */
  // /gobestads/operation/customerFundChangeDaily/queryIncomeExpenseDetail
  function getIncomeExpenseDetail(params: {
    detailType: string
    dateRange: [Dayjs, Dayjs] | string[] // 支持 Dayjs 对象或字符串数组
  }) {
    const dateRange = DateRanges.dates(params.dateRange)!

    return requestClient
      .post<IncomeExpenseDetailResponse>(
        prefix('customerFundChangeDaily/queryIncomeExpenseDetail'),
        {
          detailType: params.detailType,
          dateRange,
        },
      )
      .then((res) => {
        // 处理列表数据，确保字段完整
        return {
          top20List: (res?.top20List || []).map((item) => ({
            customerId: item.customerId || 0,
            companyName: item.companyName || '',
            operatorOpenId: item.operatorOpenId,
            managerOpenId: item.managerOpenId || '',
            amount: item.amount || 0,
            yoyPercent: item.yoyPercent || 0,
            changeAmount: item.changeAmount || 0,
          })),
          top20GrowthList: (res?.top20GrowthList || []).map((item) => ({
            customerId: item.customerId || 0,
            companyName: item.companyName || '',
            operatorOpenId: item.operatorOpenId,
            managerOpenId: item.managerOpenId || '',
            amount: item.amount || 0,
            yoyPercent: item.yoyPercent || 0,
            changeAmount: item.changeAmount || 0,
          })),
          top20DeclineList: (res?.top20DeclineList || []).map((item) => ({
            customerId: item.customerId || 0,
            companyName: item.companyName || '',
            operatorOpenId: item.operatorOpenId,
            managerOpenId: item.managerOpenId || '',
            amount: item.amount || 0,
            yoyPercent: item.yoyPercent || 0,
            changeAmount: item.changeAmount || 0,
          })),
          pendingAmount: res?.pendingAmount ?? 0,
        }
      })
  }

  return {
    getOverallIncomeExpenseCurve,
    getIncomeExpenseDetail,
  }
})
